import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  MenuItem
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    paddingTop: theme.spacing(2)
  },
  field: {
    marginBottom: theme.spacing(2)
  }
}));

const EMPLOYMENT_TYPES = [
  'Full-time',
  'Part-time',
  'Self-employed',
  'Freelance',
  'Contract',
  'Internship',
  'Apprenticeship',
  'Seasonal'
];

const LOCATION_TYPES = [
  'On-site',
  'Hybrid',
  'Remote'
];

const ExperienceDialog = ({ open, onClose, onSave, experience, loading }) => {
  const classes = useStyles();
  
  const [formData, setFormData] = useState({
    title: '',
    employmentType: '',
    company: '',
    location: '',
    locationType: '',
    isCurrentRole: false,
    startDate: '',
    endDate: '',
    description: ''
  });

  useEffect(() => {
    if (experience) {
      setFormData({
        title: experience.title || '',
        employmentType: experience.employmentType || '',
        company: experience.company || '',
        location: experience.location || '',
        locationType: experience.locationType || '',
        isCurrentRole: experience.isCurrentRole || false,
        startDate: experience.startDate || '',
        endDate: experience.endDate || '',
        description: experience.description || ''
      });
    } else {
      // Reset form when opening for new experience
      setFormData({
        title: '',
        employmentType: '',
        company: '',
        location: '',
        locationType: '',
        isCurrentRole: false,
        startDate: '',
        endDate: '',
        description: ''
      });
    }
  }, [experience, open]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    setFormData({
      ...formData,
      isCurrentRole: isChecked,
      endDate: isChecked ? '' : formData.endDate // Clear end date if current role
    });
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  const isFormValid = () => {
    return (
      formData.title.trim() &&
      formData.company.trim() &&
      formData.startDate &&
      (formData.isCurrentRole || formData.endDate)
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{experience ? 'Edit Experience' : 'Add Experience'}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Title *"
              placeholder="Ex: Software Engineer"
              fullWidth
              variant="outlined"
              value={formData.title}
              onChange={handleChange('title')}
              className={classes.field}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Employment Type"
              fullWidth
              variant="outlined"
              value={formData.employmentType}
              onChange={handleChange('employmentType')}
              className={classes.field}
            >
              <MenuItem value="">Select type</MenuItem>
              {EMPLOYMENT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Company *"
              placeholder="Ex: Google"
              fullWidth
              variant="outlined"
              value={formData.company}
              onChange={handleChange('company')}
              className={classes.field}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isCurrentRole}
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label="I am currently working in this role"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Start Date *"
              type="month"
              fullWidth
              variant="outlined"
              value={formData.startDate}
              onChange={handleChange('startDate')}
              InputLabelProps={{
                shrink: true
              }}
              className={classes.field}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="End Date"
              type="month"
              fullWidth
              variant="outlined"
              value={formData.endDate}
              onChange={handleChange('endDate')}
              disabled={formData.isCurrentRole}
              InputLabelProps={{
                shrink: true
              }}
              className={classes.field}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Location"
              placeholder="Ex: San Francisco, CA"
              fullWidth
              variant="outlined"
              value={formData.location}
              onChange={handleChange('location')}
              className={classes.field}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Location Type"
              fullWidth
              variant="outlined"
              value={formData.locationType}
              onChange={handleChange('locationType')}
              className={classes.field}
            >
              <MenuItem value="">Select type</MenuItem>
              {LOCATION_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              placeholder="Describe your responsibilities and achievements..."
              fullWidth
              multiline
              minRows={4}
              variant="outlined"
              value={formData.description}
              onChange={handleChange('description')}
              className={classes.field}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!isFormValid() || loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ExperienceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  experience: PropTypes.object,
  loading: PropTypes.bool
};

ExperienceDialog.defaultProps = {
  experience: null,
  loading: false
};

export default ExperienceDialog;

